import { useState, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { validatePhoneNumber, validateOTP, sanitizePhoneInput } from '@/utils/validation'

type LoginStep = 'phone' | 'otp'

interface UseLoginFormReturn {
  // State
  phone: string
  otp: string
  step: LoginStep
  error: string | null
  successMessage: string | null
  isLoading: boolean
  isPhoneValid: boolean
  isOTPValid: boolean

  // Actions
  setPhone: (value: string) => void
  setOtp: (value: string) => void
  handlePhoneSubmit: (e: React.FormEvent) => Promise<void>
  handleOTPSubmit: (e: React.FormEvent) => Promise<void>
  handleResendOTP: () => Promise<void>
  handleBack: () => void
  clearMessages: () => void
}

/**
 * Custom hook for managing login form state and logic
 * Separates business logic from UI components
 */
export function useLoginForm(): UseLoginFormReturn {
  const navigate = useNavigate()
  const {
    startLogin,
    verifyOTP,
    resendOTP,
    clearPendingChallenge,
    pendingChallenge,
    loading,
    error: authError,
    setError: setAuthError,
  } = useAuth()

  const [phone, setPhoneState] = useState('')
  const [otp, setOtpState] = useState('')
  const [localError, setLocalError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const step: LoginStep = pendingChallenge ? 'otp' : 'phone'
  const error = localError || authError
  const isLoading = loading

  // Validation states
  const phoneValidation = useMemo(
    () => validatePhoneNumber(phone),
    [phone]
  )
  const isPhoneValid = phoneValidation.isValid

  const otpValidation = useMemo(() => validateOTP(otp), [otp])
  const isOTPValid = otpValidation.isValid

  // Sanitize and set phone number
  const setPhone = useCallback((value: string) => {
    const sanitized = sanitizePhoneInput(value)
    setPhoneState(sanitized)
    setLocalError(null)
    setAuthError(null)
  }, [setAuthError])

  // Set OTP and clear messages
  const setOtp = useCallback(
    (value: string) => {
      setOtpState(value)
      setLocalError(null)
      setAuthError(null)
      setSuccessMessage(null)
    },
    [setAuthError]
  )

  // Clear all messages
  const clearMessages = useCallback(() => {
    setLocalError(null)
    setAuthError(null)
    setSuccessMessage(null)
  }, [setAuthError])

  // Handle phone form submission
  const handlePhoneSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      clearMessages()

      // Validate phone number
      if (!isPhoneValid) {
        setLocalError(phoneValidation.error || 'Invalid phone number')
        return
      }

      const result = await startLogin(phone)
      if (!result.success) {
        setLocalError(result.message || 'Login failed')
      }
    },
    [phone, isPhoneValid, phoneValidation, startLogin, clearMessages]
  )

  // Handle OTP verification
  const handleOTPSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      clearMessages()

      // Validate OTP
      if (!isOTPValid) {
        setLocalError(otpValidation.error || 'Invalid OTP')
        return
      }

      const result = await verifyOTP(otp)
      if (result.success) {
        navigate('/')
      } else {
        setLocalError(result.message || 'OTP verification failed')
      }
    },
    [otp, isOTPValid, otpValidation, verifyOTP, navigate, clearMessages]
  )

  // Handle resend OTP
  const handleResendOTP = useCallback(async () => {
    clearMessages()

    const result = await resendOTP()
    if (result.success) {
      setSuccessMessage('New OTP sent successfully!')
      setOtpState('')
    } else {
      setLocalError(result.message || 'Failed to resend OTP')
    }
  }, [resendOTP, clearMessages])

  // Handle back to phone step
  const handleBack = useCallback(() => {
    setOtpState('')
    clearMessages()
    clearPendingChallenge()
  }, [clearMessages, clearPendingChallenge])

  return {
    // State
    phone,
    otp,
    step,
    error,
    successMessage,
    isLoading,
    isPhoneValid,
    isOTPValid,

    // Actions
    setPhone,
    setOtp,
    handlePhoneSubmit,
    handleOTPSubmit,
    handleResendOTP,
    handleBack,
    clearMessages,
  }
}
